import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, LoadingController, ToastController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUiService {
  private translate = inject(TranslateService);
  private toastController = inject(ToastController);
  private alertController = inject(AlertController);
  private loadingController = inject(LoadingController);

  isLoading = false;

  async showLoader(): Promise<void> {
    this.isLoading = true;
    const loader = await this.loadingController.create({});
    await loader.present();
  }

  async dismissLoader(): Promise<void> {
    if (!this.isLoading) {
      return;
    }

    this.isLoading = false;
    await this.loadingController.dismiss();
  }

  async dismissToast(): Promise<void> {
    try {
      await this.toastController.dismiss();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (!/overlay does not exist/i.test(message)) {
        throw error;
      }
    }
  }

  translateCode(code: string): string {
    return this.translate.instant(code);
  }

  async presentDefaultToast(messageCode: string, durationMs = 5000): Promise<void> {
    const translatedMessage = await firstValueFrom(this.translate.get(messageCode));

    const toast = await this.toastController.create({
      position: 'top',
      cssClass: 'flash-toast',
      message: translatedMessage,
      duration: durationMs,
      buttons: [
        {
          text: '×',
          side: 'end',
          role: 'cancel',
          handler: () => {
            toast.dismiss();
          },
        },
      ],
    });

    await toast.present();
  }

  async presentErrorToast(error: unknown, durationMs = 5000): Promise<void> {
    let message = '';

    if (typeof error === 'string') {
      message = await firstValueFrom(this.translate.get(error));
    } else if (error instanceof Error) {
      message = error.message;
    } else {
      message = String(error || 'An error occurred');
    }

    const toast = await this.toastController.create({
      position: 'top',
      cssClass: ['flash-toast', 'flash-toast-error'],
      message,
      duration: durationMs,
      buttons: [
        {
          text: '×',
          side: 'end',
          role: 'cancel',
          handler: () => {
            toast.dismiss();
          },
        },
      ],
    });

    await toast.present();
  }

  async presentDefaultAlert(headerCode: string, subHeaderCode: string, messageCode: string, buttonCode: string): Promise<void> {
    const [translatedHeader, translatedSubHeader, translatedMessage, translatedButton] = await Promise.all([
      firstValueFrom(this.translate.get(headerCode)),
      firstValueFrom(this.translate.get(subHeaderCode)),
      firstValueFrom(this.translate.get(messageCode)),
      firstValueFrom(this.translate.get(buttonCode)),
    ]);

    const alert = await this.alertController.create({
      header: translatedHeader,
      subHeader: translatedSubHeader,
      message: translatedMessage,
      buttons: [translatedButton],
    });

    await alert.present();
  }
}
